<!-- Navbar -->
<nav class="navbar navbar-dark bg-dark border-bottom sticky-top">
    <div class="container">
        <span class="navbar-brand fw-bold fs-4">Binst</span>
        <span>
        <a class="mx-3" href="/">Back to Binst</a>
        <span class="text-muted small text-uppercase fw-semibold">Privacy Policy</span>
        <a class="mx-3" href="/terms-of-service">Terms of service</a>
        </span>
    </div>
</nav>

<div class="container py-5">
    <div class="row g-5">

        <!-- Sidebar -->
        <div class="col-lg-3 d-none d-lg-block">
            <div class="sticky-top pt-2" style="top: 72px;">
                <p class="text-uppercase text-muted small fw-semibold mb-3">Contents</p>
                <nav class="nav flex-column gap-1">
                    <a class="nav-link text-muted px-0 py-1 small" href="/privacy#intro">1. Introduction</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="/privacy#who">2. Who We Are</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="/privacy#collect">3. Information We Collect</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="/privacy#use">4. How We Use Your Information</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="/privacy#public">5. Public Nature of Content</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="/privacy#sharing">6. Sharing Your Information</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="/privacy#retention">7. Data Retention</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="/privacy#cookies">8. Cookies &amp; Tracking</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="/privacy#rights">9. Your Rights</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="/privacy#security">10. Security</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="/privacy#children">11. Children's Privacy</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="/privacy#changes">12. Changes to This Policy</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="/privacy#contact">13. Contact Us</a>
                </nav>
            </div>
        </div>

        <!-- Main content -->
        <div class="col-lg-9">

            <!-- Hero -->
            <div class="mb-5 pb-4 border-bottom">
                <h1 class="display-5 fw-bold mb-2">Privacy Policy</h1>
                <p class="text-muted mb-3">Effective Date: 6 May 2026</p>
                <div class="alert alert-warning" role="alert">
                    <strong>Binst is a fully public platform.</strong> Everything you post — text and images — is immediately visible to anyone in the world. There are no private posts or privacy settings.
                </div>
            </div>

            <!-- 1. Introduction -->
            <section id="intro" class="mb-5">
                <h2 class="h4 fw-bold mb-3">1. Introduction</h2>
                <p class="text-muted">Welcome to Binst. Binst is a public social media platform designed to function as an open, public billboard — a space where users can share text and images with the world. There are no private posts or privacy controls; all content posted on Binst is visible to everyone.</p>
                <p class="text-muted">This Privacy Policy explains how we collect, use, and handle your personal information when you access or use the Binst platform. By using Binst, you agree to the terms described in this policy.</p>
            </section>

            <hr class="mb-5" />

            <!-- 2. Who We Are -->
            <section id="who" class="mb-5">
                <h2 class="h4 fw-bold mb-3">2. Who We Are</h2>
                <p class="text-muted">Binst is operated as a public social media platform. For any privacy-related queries, you can contact us at:</p>
                <ul class="list-unstyled text-muted">
                    <li><strong>Email:</strong> support&#64;binst.net</li>
                    <li><strong>Website:</strong> binst.sewell.digital</li>
                </ul>
            </section>

            <hr class="mb-5" />

            <!-- 3. Information We Collect -->
            <section id="collect" class="mb-5">
                <h2 class="h4 fw-bold mb-3">3. Information We Collect</h2>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">3.1 Information from Google OAuth Login</h3>
                <p class="text-muted">When you sign in to Binst using your Google account, we receive and store the following information from Google:</p>
                <ul class="text-muted">
                    <li>Your Google account display name</li>
                    <li>Your Google account email address</li>
                    <li>Your Google profile picture</li>
                </ul>
                <p class="text-muted">You may update this information at any time through your Binst profile settings. We do not retain historical versions of your profile data — updates overwrite your previous information permanently.</p>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">3.2 Content You Post</h3>
                <p class="text-muted">Because Binst is a fully public platform, any text or images you upload are immediately and permanently visible to all users and visitors, including those who are not logged in. Please do not post content you do not wish to be made public.</p>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">3.3 Automatically Collected Information</h3>
                <p class="text-muted">When you use Binst, we may automatically collect certain technical information, including:</p>
                <ul class="text-muted">
                    <li>IP address</li>
                    <li>Browser type and version</li>
                    <li>Device type and operating system</li>
                    <li>Pages visited and time spent on the platform</li>
                    <li>Referring URLs</li>
                </ul>
                <p class="text-muted">This information is used to maintain and improve the platform and is not used to identify you personally.</p>
            </section>

            <hr class="mb-5" />

            <!-- 4. How We Use Your Information -->
            <section id="use" class="mb-5">
                <h2 class="h4 fw-bold mb-3">4. How We Use Your Information</h2>
                <p class="text-muted">We use the information we collect for the following purposes:</p>
                <ul class="text-muted">
                    <li>To create and manage your Binst account</li>
                    <li>To display your name and profile picture alongside your public posts</li>
                    <li>To communicate with you regarding your account (e.g. security notices)</li>
                    <li>To maintain, operate, and improve the Binst platform</li>
                    <li>To detect and prevent abuse, spam, or fraudulent activity</li>
                    <li>To comply with applicable legal obligations</li>
                </ul>
            </section>

            <hr class="mb-5" />

            <!-- 5. Public Nature of Content -->
            <section id="public" class="mb-5">
                <h2 class="h4 fw-bold mb-3">5. Public Nature of All Content</h2>
                <div class="alert alert-danger" role="alert">
                    <strong>Important:</strong> Binst has no privacy settings. All content is public by design.
                </div>
                <p class="text-muted">By posting content on Binst, you acknowledge and accept that:</p>
                <ul class="text-muted">
                    <li>All content you post is immediately visible to any person in the world</li>
                    <li>Content may be indexed by search engines</li>
                    <li>Content may be shared, copied, or viewed by third parties</li>
                </ul>
                <p class="text-muted">You are solely responsible for the content you choose to publish on Binst. We strongly advise you not to post personal, sensitive, or confidential information.</p>
            </section>

            <hr class="mb-5" />

            <!-- 6. Sharing Your Information -->
            <section id="sharing" class="mb-5">
                <h2 class="h4 fw-bold mb-3">6. Sharing Your Information</h2>
                <p class="text-muted">We do not sell your personal information. We may share your information in the following limited circumstances:</p>
                <ul class="text-muted">
                    <li><strong>Service providers:</strong> Trusted third-party providers who assist in operating the platform (e.g. hosting, analytics). These providers are contractually obligated to handle your data securely and only as instructed.</li>
                    <li><strong>Legal obligations:</strong> If required by law or in response to valid legal requests from public authorities.</li>
                    <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred. We will notify you of any such change.</li>
                    <li><strong>Protection of rights:</strong> Where necessary to investigate, prevent, or act on illegal activity or violations of our terms.</li>
                </ul>
            </section>

            <hr class="mb-5" />

            <!-- 7. Data Retention -->
            <section id="retention" class="mb-5">
                <h2 class="h4 fw-bold mb-3">7. Data Retention</h2>
                <p class="text-muted">We retain your account information (name, email, profile picture) for as long as your account remains active. If you delete your account, we will remove your personal information from our active systems, though some data may remain in backups for a limited period before being purged.</p>
                <p class="text-muted">Profile updates overwrite previous data — we do not store or retain past versions of your name, email, or profile picture.</p>
                <p class="text-muted">Content you have posted publicly may remain on the platform after account deletion unless you manually delete it before closing your account.</p>
            </section>

            <hr class="mb-5" />

            <!-- 8. Cookies -->
            <section id="cookies" class="mb-5">
                <h2 class="h4 fw-bold mb-3">8. Cookies &amp; Tracking</h2>
                <p class="text-muted">Binst uses cookies and similar technologies to maintain your login session and improve your experience. These include:</p>
                <ul class="text-muted">
                    <li><strong>Session cookies:</strong> Required for you to remain logged in while using the platform.</li>
                    <li><strong>Analytics cookies:</strong> Used to understand how users interact with the platform (e.g. pages visited, time spent).</li>
                </ul>
                <p class="text-muted">You can disable cookies in your browser settings, but doing so may prevent you from logging in or using certain features of Binst.</p>
            </section>

            <hr class="mb-5" />

            <!-- 9. Your Rights -->
            <section id="rights" class="mb-5">
                <h2 class="h4 fw-bold mb-3">9. Your Rights</h2>
                <p class="text-muted">Depending on your jurisdiction, you may have certain rights regarding your personal data, including:</p>
                <ul class="text-muted">
                    <li><strong>Access:</strong> The right to request a copy of the personal data we hold about you.</li>
                    <li><strong>Correction:</strong> The right to update or correct inaccurate personal data.</li>
                    <li><strong>Deletion:</strong> The right to request that we delete your personal data.</li>
                    <li><strong>Objection:</strong> The right to object to certain types of data processing.</li>
                </ul>
                <p class="text-muted">To exercise any of these rights, please contact us at <a href="mailto:support@binst.net">support&#64;binst.net</a>. We will respond to your request within 30 days.</p>
                <div class="row g-3 mt-2">
                    <div class="col-md-6">
                        <div class="card border h-100">
                            <div class="card-body">
                                <h5 class="card-title h6 fw-semibold">🇿🇦 South Africa</h5>
                                <p class="card-text text-muted small mb-0">Your rights are governed by the <strong>Protection of Personal Information Act (POPIA)</strong>, Act 4 of 2013.</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="card border h-100">
                            <div class="card-body">
                                <h5 class="card-title h6 fw-semibold">🇪🇺 European Economic Area</h5>
                                <p class="card-text text-muted small mb-0">Your rights are governed by the <strong>General Data Protection Regulation (GDPR)</strong>.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <hr class="mb-5" />

            <!-- 10. Security -->
            <section id="security" class="mb-5">
                <h2 class="h4 fw-bold mb-3">10. Security</h2>
                <p class="text-muted">We take reasonable technical and organisational measures to protect your personal information from unauthorised access, loss, or misuse. However, no method of transmission over the internet is completely secure, and we cannot guarantee absolute security.</p>
                <p class="text-muted">We recommend using a strong, unique password for your Google account and enabling two-factor authentication for added protection.</p>
            </section>

            <hr class="mb-5" />

            <!-- 11. Children's Privacy -->
            <section id="children" class="mb-5">
                <h2 class="h4 fw-bold mb-3">11. Children's Privacy</h2>
                <p class="text-muted">Binst is not intended for use by individuals under the age of 13. We do not knowingly collect personal information from children. If we become aware that we have inadvertently collected data from a child under 13, we will take steps to delete such information promptly.</p>
            </section>

            <hr class="mb-5" />

            <!-- 12. Changes -->
            <section id="changes" class="mb-5">
                <h2 class="h4 fw-bold mb-3">12. Changes to This Policy</h2>
                <p class="text-muted">We may update this Privacy Policy from time to time. When we do, we will update the effective date at the top of this document. Where changes are significant, we will notify registered users via email or a prominent notice on the platform.</p>
                <p class="text-muted">Your continued use of Binst after any changes to this policy constitutes your acceptance of the updated terms.</p>
            </section>

            <hr class="mb-5" />

            <!-- 13. Contact -->
            <section id="contact" class="mb-5">
                <h2 class="h4 fw-bold mb-3">13. Contact Us</h2>
                <p class="text-muted">If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please reach out to us:</p>
                <div class="card border mt-3">
                    <div class="card-body">
                        <h5 class="card-title h6 fw-semibold mb-3">Binst Privacy Team</h5>
                        <p class="mb-1 text-muted small"><strong>Email:</strong> <a href="mailto:support@binst.net">support&#64;binst.net</a></p>
                        <p class="mb-0 text-muted small"><strong>Website:</strong> <a href="https://binst.sewell.digital" target="_blank">binst.sewell.digital</a></p>
                    </div>
                </div>
            </section>

        </div>
    </div>
</div>

<!-- Footer -->
<footer class="border-top bg-dark mt-5 py-4">
    <div class="container d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
        <span class="fw-bold">Binst</span>
        <span class="text-muted small">&copy; 2026 Binst. All rights reserved.</span>
        <span class="text-muted small">Effective: 6 May 2026</span>
    </div>
</footer>
